import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, fmtMoney, fmtDate, EmptyState, SkeletonRow, corporateInput } from "@/components/Shared";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import RowMenu from "@/components/RowMenu";
import { Plus, CheckCircle, ArrowSquareOut, Copy, Trash, ArrowsClockwise, EnvelopeSimple, Receipt, Warning, ArrowUUpLeft, FilePdf, CloudArrowDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { API as API_BASE } from "@/lib/api";

// Computed at module load — purity rule wants these outside the component.
const TODAY_ISO = () => new Date().toISOString().slice(0, 10);
const DEFAULT_DUE_ISO = () => new Date(Date.now() + 14 * 86400 * 1000).toISOString().slice(0, 10);

export default function Invoices() {
  const { t, lang } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(() => ({
    client_id: "", amount: "", description: "", due_date: DEFAULT_DUE_ISO(),
    issue_date: TODAY_ISO(), generate_payment_link: true,
  }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices", { params: tab !== "all" ? { status: tab } : {} });
      setInvoices(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, []);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      const { data } = await api.post("/invoices", payload);
      toast.success(`Factura creada${data.square_payment_link_url ? " · link de pago listo" : ""}`);
      setOpen(false);
      setForm({ ...form, client_id: "", amount: "", description: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/invoices/${id}/mark-paid`);
      toast.success("Factura marcada como pagada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const regenLink = async (id) => {
    try {
      await api.post(`/invoices/${id}/regenerate-link`);
      toast.success("Link de pago regenerado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeInv = async (id) => {
    if (!confirm("¿Eliminar esta factura?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Factura eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendEmail = async (id) => {
    try {
      const { data } = await api.post(`/invoices/${id}/send-email`);
      if (data.mocked) {
        toast.success(`Email registrado (modo MOCK) para ${data.to} · activa Resend en .env para envío real`);
      } else {
        toast.success(`Email enviado a ${data.to}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copiado al portapapeles");
  };

  const [refundOpen, setRefundOpen] = useState(false);
  const [refundInv, setRefundInv] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);

  const openRefund = (inv) => {
    setRefundInv(inv);
    const maxAmount = inv.total_due || inv.amount;
    const alreadyRefunded = inv.refund_amount || 0;
    setRefundAmount((maxAmount - alreadyRefunded).toFixed(2));
    setRefundReason("");
    setRefundOpen(true);
  };

  const doRefund = async () => {
    setRefunding(true);
    try {
      const { data } = await api.post(`/invoices/${refundInv.id}/refund`, {
        amount: Number(refundAmount),
        reason: refundReason || undefined,
      });
      toast.success(`Reembolso ${data.full_refund ? "completo" : "parcial"} procesado · ${fmtMoney(data.amount)}`);
      setRefundOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRefunding(false);
    }
  };

  const backfillSquare = async () => {
    const beginIso = prompt(
      "Importar todos los pagos COMPLETED de Square desde esta fecha (YYYY-MM-DD):",
      "2025-10-01"
    );
    if (!beginIso) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(beginIso)) {
      toast.error("Formato inválido. Usa YYYY-MM-DD");
      return;
    }
    if (!confirm(`Esto creará facturas con status=paid por cada pago COMPLETED de Square desde ${beginIso}.\n\nLos clientes sin match se crearán como stubs (puedes editarlos luego).\nLos refunds se guardan aparte en square_refunds.\n\nEs idempotente: re-ejecutar no duplica nada.\n\n¿Continuar?`)) return;
    const tid = toast.loading("Importando histórico de Square… (puede tardar minutos)");
    try {
      const { data } = await api.post("/square/backfill-payments", null, { params: { begin_date: beginIso }, timeout: 300000 });
      toast.dismiss(tid);
      toast.success(
        `Square histórico: ${data.invoices_created} facturas · ${data.stub_clients_created} clientes stub · ${data.refunds_logged} refunds (de ${data.totals.payments_in_square} pagos)`,
        { duration: 8000 }
      );
      if (data.errors?.length) toast.error(`${data.errors.length} errores — primero: ${data.errors[0]}`);
      load();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(formatApiError(e.response?.data?.detail) || "Error al importar");
    }
  };

  return (
    <PageShell
      section={t("nav.group.operation")}
      page={t("inv.title")}
      context={`${invoices.length} ${lang === "en" ? "in view" : "en vista"}`}
      testid="invoices-toolbar"
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={backfillSquare}
            data-testid="invoices-square-backfill"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 hover:text-slate-900 dark:hover:text-white rounded transition-colors h-9"
            title="Importar histórico de pagos Square (Oct 2025 → hoy)"
          >
            <CloudArrowDown size={14} /> <span className="hidden sm:inline">Square histórico</span><span className="sm:hidden">Square</span>
          </button>
          <Button
            onClick={() => setOpen(true)}
            disabled={clients.length === 0}
            data-testid="add-invoice-button"
            className="bg-slate-900 hover:bg-slate-800 text-white h-9"
          >
            <Plus size={15} className="mr-1.5" /> {t("inv.new")}
          </Button>
        </div>
      }
    >

      {clients.length === 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-4 py-3 text-sm">
          {lang === "en" ? "You must register at least one client before creating invoices." : "Primero debes registrar al menos un cliente antes de crear facturas."}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList data-testid="invoices-tabs">
          <TabsTrigger value="all" data-testid="tab-all">{t("inv.tab.all")}</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">{t("inv.tab.pending")}</TabsTrigger>
          <TabsTrigger value="paid" data-testid="tab-paid">{t("inv.tab.paid")}</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tab-overdue">{t("inv.tab.overdue")}</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">{t("inv.col.client")}</th>
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">{t("inv.col.description")}</th>
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">{t("inv.col.issue")}</th>
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">{t("inv.col.due")}</th>
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 text-right">{t("inv.col.amount")}</th>
              <th className="px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-slate-500">{t("inv.col.status")}</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={7} />)}
            {!loading && invoices.length === 0 && (
              <tr><td colSpan={7}>
                <EmptyState
                  icon={Receipt}
                  title={tab === "all" ? t("inv.empty.title") : (lang === "en" ? "No invoices in this category" : "Sin facturas en esta categoría")}
                  message={tab === "all"
                    ? t("inv.empty.msg")
                    : (lang === "en" ? "Switch tabs or create a new invoice." : "Cambia de pestaña o crea una nueva factura.")}
                  action={
                    clients.length > 0 && (
                      <Button onClick={() => setOpen(true)} data-testid="empty-add-invoice" className="bg-slate-900 hover:bg-slate-800 text-white h-9">
                        <Plus size={15} className="mr-1.5" /> {t("inv.new")}
                      </Button>
                    )
                  }
                />
              </td></tr>
            )}
            {invoices.map((inv) => {
              const flags = inv.fraud_flags || [];
              const hasFraud = flags.length > 0;
              const riskHigh = flags.includes("risk_high");
              return (
              <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="hover:bg-slate-50">
                <td className="px-5 py-4 font-medium text-slate-900">
                  <div className="flex items-center gap-2">
                    {hasFraud && (
                      <Warning
                        size={16}
                        weight="fill"
                        className={riskHigh ? "text-red-600" : "text-amber-500"}
                        title={`Alertas: ${flags.join(", ")}`}
                      />
                    )}
                    <span>{inv.client_name}</span>
                  </div>
                </td>
                <td className="px-5 py-4 text-slate-600 max-w-xs truncate">{inv.description || "—"}</td>
                <td className="px-5 py-4 text-slate-600">{fmtDate(inv.issue_date)}</td>
                <td className="px-5 py-4 text-slate-600">{fmtDate(inv.due_date)}</td>
                <td className="px-5 py-4 text-right font-mono font-semibold text-slate-900">
                  {fmtMoney(inv.total_due || inv.amount)}
                  {inv.reconnection_fee_amount > 0 && (
                    <div className="text-[10px] text-red-600 font-normal mt-0.5">incl. ${inv.reconnection_fee_amount} reconex.</div>
                  )}
                </td>
                <td className="px-5 py-4"><StatusBadge status={inv.status} /></td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <RowMenu
                    testid={`invoice-menu-${inv.id}`}
                    items={[
                      inv.square_payment_link_url && {
                        label: lang === "en" ? "Open payment link" : "Abrir link de pago",
                        icon: ArrowSquareOut,
                        href: inv.square_payment_link_url,
                        testid: `pay-link-${inv.id}`,
                      },
                      inv.square_payment_link_url && {
                        label: lang === "en" ? "Copy link" : "Copiar link",
                        icon: Copy,
                        onClick: () => copyLink(inv.square_payment_link_url),
                        testid: `copy-link-${inv.id}`,
                      },
                      !inv.square_payment_link_url && inv.status !== "paid" && {
                        label: lang === "en" ? "Generate payment link" : "Generar link de pago",
                        icon: ArrowsClockwise,
                        onClick: () => regenLink(inv.id),
                        testid: `regen-link-${inv.id}`,
                      },
                      inv.status !== "paid" && {
                        label: lang === "en" ? "Email client" : "Enviar email al cliente",
                        icon: EnvelopeSimple,
                        onClick: () => sendEmail(inv.id),
                        testid: `send-email-${inv.id}`,
                      },
                      inv.status !== "paid" && {
                        label: lang === "en" ? "Mark as paid" : "Marcar como pagada",
                        icon: CheckCircle,
                        onClick: () => markPaid(inv.id),
                        testid: `mark-paid-${inv.id}`,
                      },
                      inv.status === "paid" && {
                        label: lang === "en" ? "Refund payment" : "Reembolsar pago",
                        icon: ArrowUUpLeft,
                        onClick: () => openRefund(inv),
                        testid: `refund-${inv.id}`,
                      },
                      { type: "separator" },
                      {
                        label: lang === "en" ? "Download PDF" : "Descargar PDF",
                        icon: FilePdf,
                        href: `${API_BASE}/invoices/${inv.id}/pdf`,
                        testid: `pdf-${inv.id}`,
                      },
                      { type: "separator" },
                      {
                        label: lang === "en" ? "Delete" : "Eliminar",
                        icon: Trash,
                        onClick: () => removeInv(inv.id),
                        danger: true,
                        testid: `delete-invoice-${inv.id}`,
                      },
                    ].filter(Boolean)}
                  />
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="invoice-form-dialog">
          <DialogHeader><DialogTitle>Nueva factura</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => {
                const c = clients.find((x) => x.id === v);
                setForm({ ...form, client_id: v, amount: form.amount || c?.monthly_fee || "" });
              }}>
                <SelectTrigger data-testid="invoice-client-select" className="h-11 border-slate-200"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Monto (USD) *</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="invoice-amount-input" className={corporateInput} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Fecha emisión</Label>
              <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="invoice-issue-input" className={corporateInput} />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Vencimiento *</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due-input" className={corporateInput} />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Descripción</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="invoice-desc-input" className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
            </div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.generate_payment_link}
                onChange={(e) => setForm({ ...form, generate_payment_link: e.target.checked })}
                data-testid="invoice-genlink-checkbox"
                className="rounded border-slate-300"
              />
              Generar link de pago con Square automáticamente
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleCreate}
              disabled={saving || !form.client_id || !form.amount || !form.due_date}
              data-testid="save-invoice-button"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {saving ? "Creando..." : "Crear factura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Reembolsar factura</DialogTitle>
          </DialogHeader>
          {refundInv && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-medium">{refundInv.client_name}</span></div>
                <div className="flex justify-between mt-1"><span className="text-slate-500">Monto cobrado</span><span className="font-mono font-semibold">{fmtMoney(refundInv.total_due || refundInv.amount)}</span></div>
                {refundInv.refund_amount > 0 && (
                  <div className="flex justify-between mt-1"><span className="text-slate-500">Ya reembolsado</span><span className="font-mono text-rose-600">{fmtMoney(refundInv.refund_amount)}</span></div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Monto a reembolsar (USD) *</Label>
                <Input type="number" step="0.01" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="refund-amount-input" className={corporateInput} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Razón</Label>
                <Textarea rows={2} value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Opcional · ej. Cargo duplicado" data-testid="refund-reason-input" className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>Cancelar</Button>
            <Button
              onClick={doRefund}
              disabled={refunding || !refundAmount || Number(refundAmount) <= 0}
              data-testid="confirm-refund-button"
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {refunding ? "Procesando..." : "Reembolsar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
